import React, { useContext, useRef, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import useClassroom from "../useClassroom";
import Loader from "../../../../../components/Loader/Loader";
import PageWrapper from "../../../../../components/PageWrapper/PageWrapper";
import PageHeader from "../../../../../components/PageHeader/PageHeader";
import { sessionStore } from "../../../../../contexts/SessionContext";
import { configStore } from "../../../../../contexts/ConfigContext";
import { Modal } from "@evidenceb/athena-common/design-system/Modal";
import { Button } from "@evidenceb/athena-common/design-system/Button";
import { useFeatureFlag } from "@evidenceb/athena-common/modules/FeatureFlags";
import { VisuallyHidden } from "@evidenceb/athena-common/ui/VisuallyHidden";
import {
    ClassroomCreatorModal,
    ClassroomCreatorButton,
} from "../ClassroomCreator/ClassroomCreator";
import { Classroom, Group, UserType } from "../../../../../interfaces/User";
import { useOverlayTriggerState } from "react-stately";
import { Link, useHistory } from "react-router-dom";
import SpecimenHelper from "../../SpecimenHelper/SpecimenHelper";
import ContainerBlock from "../../../../../design-system-components/ContainerBlock/ContainerBlock";
import PlaceholderCard from "../../../../Home/PlaceholderCard/PlaceholderCard";
import StudentCollectionList from "../../../../../components/StudentCollectionList/StudentCollectionList";
import { useGroups } from "../../Group/api/useQueries";
import { commonMessages, pageTitles } from "../../../../../utils/messages";
import TabTitle from "../../../../../components/TabTitle/TabTitle";
import cn from "classnames";
import { useDashboardRouter } from "../../routing";
import Grid from "../../../../../components/Grid/Grid";
import GridColumn from "../../../../../components/Grid/GridColumn";
import StudentSearch from "../../StudentSearch/StudentSearch";
import {
    getInformationBanner,
    tempGarFilter,
} from "../../../../../utils/dataRetrieval";
import useOnlineStatus from "../../../../../hooks/useOnlineStatus";
import { useHasWorkmode } from "../../../../../hooks/useWorkmode";
import { InformationBannerType } from "../../../../../design-system-components/InformationBanner/type";
import InformationBanner from "../../../../../design-system-components/InformationBanner/InformationBanner";
import { getAssetUrl } from "@evidenceb/athena-common/modules/Assets";
import useAssetsDetails from "../../../../../hooks/useAssetsDetails";
import "./ClassroomList.scss";
import { WorkModeType } from "@evidenceb/athena-common/interfaces/Workmode";

const ClassroomList = () => {
    const {
        session: { classrooms, school, appProvider },
    } = useContext(sessionStore);
    const isOnline = useOnlineStatus();
    const history = useHistory();
    const {
        config: { features },
    } = useContext(configStore);
    const intl = useIntl();
    const { getUrl } = useClassroom();
    const classroomCreatorModal = useOverlayTriggerState({});
    const createdClassroomModal = useOverlayTriggerState({});
    const groupManagementFlag = useFeatureFlag("groupManagement");
    const classManagementFlag = useFeatureFlag("classManagement");
    const isSpecimen = useFeatureFlag("isSpecimen");
    const { createGroup: createGroupUrl, studentDetails: studentDetailsUrl } =
        useDashboardRouter();
    const hasSoloAIMode = useHasWorkmode(WorkModeType.SoloAI, UserType.Student);

    const [classroomCreatedData, setClassroomCreatedData] = useState<{
        id: string;
        code: string;
    }>({ id: "", code: "" });

    const onClassroomCreationEnd = (classroom: Classroom) => {
        if (!classrooms) return;
        setClassroomCreatedData({
            id: classroom.id,
            code: classroom.code,
        });
        createdClassroomModal.open();
        classroomCreatorModal.close();
    };

    const onModalDismiss = () => {
        createdClassroomModal.close();
    };

    const {
        data: groups,
        isLoading: isGroupsLoading,
        isSuccess: isGroupsSuccess,
    } = useGroups({ enabled: groupManagementFlag });

    const goToStudentDetailsPage = (classroomId: string, studentId: string) => {
        const url = studentDetailsUrl(studentId, classroomId);
        history.push(url);
    };

    const isInitiallyLoading = useRef(isGroupsLoading);

    const informationBanners = features.informationBanners;
    const classroomListBanner =
        informationBanners?.length &&
        getInformationBanner(
            InformationBannerType.CLASSROOM_LIST,
            informationBanners
        );
    const assetDetails = useAssetsDetails();
    const classroomPlaceholder =
        features.images?.dashboard?.teacher?.collection?.classrooms;
    const groupPlaceholder =
        features.images?.dashboard?.teacher?.collection?.group;
    return (
        <>
            {classroomListBanner && (
                <InformationBanner data={classroomListBanner} />
            )}
            <TabTitle title={pageTitles.teacherDashboardClassroomList} />
            <PageWrapper className="classroom-list-container">
                <Grid className="classroom-list__header-container">
                    <GridColumn col={12}>
                        <PageHeader
                            title={{
                                content:
                                    school ??
                                    intl.formatMessage(
                                        pageTitles.teacherDashboardClassroomList
                                    ),
                                size: "large",
                            }}
                            noBackBtn={true}
                            background={
                                features.images?.dashboard?.teacher?.header &&
                                getAssetUrl(
                                    features.images.dashboard.teacher.header,
                                    assetDetails
                                )
                            }
                        >
                            <>
                                {hasSoloAIMode && (
                                    <StudentSearch
                                        className="classroom-list__student-search"
                                        onClick={(classroomId, studentId) =>
                                            goToStudentDetailsPage(
                                                classroomId,
                                                studentId
                                            )
                                        }
                                    />
                                )}
                            </>
                        </PageHeader>
                    </GridColumn>
                </Grid>

                {classrooms && classrooms.length > 0 ? (
                    <ContainerBlock className="classroom-list__block">
                        <div
                            className={cn(
                                "classroom-list__block__heading",
                                !groupManagementFlag && "--no-title"
                            )}
                        >
                            {groupManagementFlag && (
                                <h2>
                                    {intl.formatMessage(messages.yourClasses)}
                                </h2>
                            )}
                            {!isSpecimen &&
                                classManagementFlag &&
                                classrooms &&
                                classrooms.length > 0 &&
                                isOnline && (
                                    <ClassroomCreatorButton
                                        state={classroomCreatorModal}
                                    />
                                )}
                        </div>
                        <StudentCollectionList
                            collection={classrooms.filter((classroom) =>
                                tempGarFilter(
                                    classroom.external_id,
                                    appProvider
                                )
                            )}
                            type="classroom"
                            className="classroom-list__block_content"
                        />
                    </ContainerBlock>
                ) : (
                    <PlaceholderCard
                        title={intl.formatMessage(messages.yourClasses)}
                        content={intl.formatMessage(
                            messages.noClassroomsMessage
                        )}
                        backgroundImg={classroomPlaceholder}
                        button={
                            classManagementFlag
                                ? {
                                      variant: "custom",
                                      colors: {
                                          background:
                                              "var(--element-teacher-default)",
                                          text: "var(--text-main-inverted)",
                                          hoverBackground:
                                              "var(--element-button-custom-hover)",
                                      },
                                      label: intl.formatMessage(
                                          messages.createClass
                                      ),
                                      onPress: () =>
                                          classroomCreatorModal.open(),
                                  }
                                : undefined
                        }
                        size="medium"
                    />
                )}

                {groupManagementFlag &&
                    ((isGroupsSuccess && groups.length > 0) ||
                        isGroupsLoading) && (
                        <ContainerBlock className="classroom-list__block">
                            <div className="classroom-list__block__heading">
                                <h2>
                                    {intl.formatMessage(messages.yourGroups)}
                                </h2>
                                {isOnline && (
                                    <Button
                                        label={intl.formatMessage(
                                            messages.addGroup
                                        )}
                                        asRouterLink={{
                                            to: createGroupUrl,
                                        }}
                                    />
                                )}
                            </div>
                            <Loader
                                isLoading={isGroupsLoading}
                                loadingMessage={intl.formatMessage(
                                    messages.loadingGroups
                                )}
                                loadedMessage={intl.formatMessage(
                                    messages.groupsLoaded
                                )}
                            >
                                {isGroupsSuccess && (
                                    <StudentCollectionList
                                        collection={groups as Group[]}
                                        type="group"
                                        className="classroom-list__block_content"
                                    />
                                )}
                            </Loader>
                        </ContainerBlock>
                    )}
                {isGroupsSuccess && groups.length === 0 && (
                    <>
                        {isInitiallyLoading.current && (
                            <VisuallyHidden aria-live="polite">
                                {intl.formatMessage(messages.groupsLoaded)}
                            </VisuallyHidden>
                        )}
                        <PlaceholderCard
                            title={intl.formatMessage(messages.yourGroups)}
                            content={intl.formatMessage(
                                messages.yourGroupsEmptyMessage
                            )}
                            backgroundImg={groupPlaceholder}
                            button={{
                                variant: "custom",
                                colors: {
                                    background:
                                        "var(--element-teacher-default)",
                                    text: "var(--text-main-inverted)",
                                    hoverBackground:
                                        "var(--element-button-custom-hover)",
                                },
                                label: intl.formatMessage(messages.createGroup),
                                asRouterLink: {
                                    to: createGroupUrl,
                                },
                            }}
                            size="medium"
                        />
                    </>
                )}
            </PageWrapper>

            {isSpecimen && (
                <SpecimenHelper
                    dialogTitle={intl.formatMessage(
                        messages.specimenClassroomHelpDialogTitle
                    )}
                    dialogDescription={intl.formatMessage(
                        messages.specimenClassroomHelpDialogDescription,
                        {
                            educational_space_link: (
                                <Link
                                    to={`/${features.specimen?.educationSpaceLink}`}
                                >
                                    {intl.formatMessage(
                                        messages.specimenClassroomHelpDialogLinkLabel
                                    )}
                                </Link>
                            ),
                        }
                    )}
                />
            )}
            {classroomCreatorModal.isOpen && (
                <ClassroomCreatorModal
                    state={classroomCreatorModal}
                    onEnd={onClassroomCreationEnd}
                    className="classroom-list__classroom-creator"
                />
            )}
            {createdClassroomModal.isOpen && (
                <Modal
                    open={createdClassroomModal.isOpen}
                    onClose={onModalDismiss}
                    title={intl.formatMessage(messages.classroomCreated)}
                    aria-label={intl.formatMessage(messages.classroomCreated)}
                    footerButtons={[
                        {
                            label: intl.formatMessage(
                                messages.classroomCreatedContinue
                            ),
                            icons: [
                                {
                                    placement: "right",
                                    path: "arrow_right",
                                },
                            ],
                            asRouterLink: {
                                to: getUrl(
                                    "classroom",
                                    classroomCreatedData.id
                                ),
                            },
                        },
                    ]}
                >
                    <div className="classroom-created-modal-content">
                        <div className="classroom-created-modal-content__code">
                            {intl.formatMessage(commonMessages.classroomCode)}
                            <span>{classroomCreatedData.code}</span>
                        </div>
                        <p>
                            {intl.formatMessage(
                                messages.classroomCreatedMessage
                            )}
                        </p>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default ClassroomList;

const messages = defineMessages({
    pageTitle: {
        id: "dashboard-classes-pageTitle",
        defaultMessage: "Classes",
    },
    yourClasses: {
        id: "dashboard-classes-yourClasses",
        defaultMessage: "Your classes",
    },
    noClassroomsMessage: {
        id: "teacher-landing-page-noClassroomsMessage",
        defaultMessage: "You have no classrooms yet.",
    },
    createClass: {
        id: "dashboard-classList-createClass",
        defaultMessage: "Create a class",
    },
    yourGroups: {
        id: "dashboard-classList-yourGroups",
        defaultMessage: "Your groups",
    },
    addGroup: {
        id: "dashboard-classList-addGroup",
        defaultMessage: "Add a group",
    },
    yourGroupsEmptyMessage: {
        id: "dashboard-classList-yourGroupsEmptyMessage",
        defaultMessage:
            "Create groups from the students in your classes. You will then be able to track their progress on each resource you assigned to them.",
    },
    createGroup: {
        id: "dashboard-classList-createGroup",
        defaultMessage: "Create a group",
    },

    classroomCreated: {
        id: "dashboard-classList-form-success",
        defaultMessage: "The class has been successfully added",
    },
    classroomCreatedMessage: {
        id: "dashboard-classList-form-message",
        defaultMessage:
            "This code has to be provided to the students before their first log in. The code will always be displayed in the dashboard.",
    },
    classroomCreatedContinue: {
        id: "dashboard-classList-form-continue",
        defaultMessage: "Continue",
    },
    specimenClassroomHelpDialogTitle: {
        id: "specimen-dashboard-classlist-help-dialog-title",
        defaultMessage: "Welcome to the dashboard !",
    },
    specimenClassroomHelpDialogDescription: {
        id: "specimen-dashboard-classlist-help-dialog-description",
        defaultMessage:
            "Here you will find all your classes with an overview of their progress in the different modules. To learn more, go to the {educational_space_link}.",
    },
    specimenClassroomHelpDialogLinkLabel: {
        id: "specimen-dashboard-classlist-help-dialog-link-label",
        defaultMessage: "pedagogical support page",
    },
    loadingGroups: {
        id: "dashboard-classList-loadingGroups",
        defaultMessage: "Loading groups data...",
    },
    groupsLoaded: {
        id: "dashboard-classList-loadedGroups",
        defaultMessage: "Groups loaded",
    },
});
